/**
 * 渐变（M2-12 / DESIGN.md §6.2「渐变（线性/径向/角度/对称/菱形）」）。
 *
 * 纯函数：给定几何（from→to）、色标与插值空间，逐像素求色。
 * GPU 着色器（src/gpu/shaders/gradient）实现同一套公式，
 * playwright 对拍保证两边不漂移。
 *
 * 插值空间由参数控制（验收标准 1）：
 * - 'srgb'：直接在 sRGB 字节上插值（多数软件的默认，视觉均匀性差）
 * - 'linear'：转线性光插值再转回（物理正确，中段不会发灰）
 */

export type GradientKind = 'linear' | 'radial' | 'angle' | 'reflected' | 'diamond';

export interface GradientStop {
  /** 0–1。 */
  readonly offset: number;
  /** sRGB 0–255 + alpha 0–255。 */
  readonly color: readonly [number, number, number, number];
}

export interface GradientSpec {
  readonly kind: GradientKind;
  /** 起点/终点，文档空间像素。 */
  readonly from: { readonly x: number; readonly y: number };
  readonly to: { readonly x: number; readonly y: number };
  readonly stops: readonly GradientStop[];
  readonly space: 'srgb' | 'linear';
  /** 反向（工具选项）。 */
  readonly reverse?: boolean;
  /**
   * 仿色（工具选项）：加一点有序抖动再取整，打散色标之间大片纯色渐变
   * 常见的 8 位色阶断层（banding）。幅度很小（±0.5 级 LSB 内），
   * 看不出噪点，只是让边界不再是一条锐利的阶梯。
   */
  readonly dither?: boolean;
}

/** 4×4 有序抖动矩阵（Bayer），归一化到 [-0.5, 0.5)。 */
const BAYER4X4: readonly (readonly number[])[] = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
];

/**
 * -0.5..+0.5（含两端）——分母是 15 不是 16：色标颜色本身已经是整数字节，
 * 抖动是在 `Math.round` 之前叠加的扰动，必须能摸到 ±0.5 这个整数舍入的
 * 边界，纯色大色块里才会真的冒出偶尔 ±1 级的像素（分母若是 16，最大
 * 幅度只有 0.4375，永远舍入回原值，抖动等于白加）。
 */
function ditherOffset(x: number, y: number): number {
  const v = BAYER4X4[y & 3]![x & 3]!;
  return v / 15 - 0.5;
}

export function srgbToLinear(byte: number): number {
  const c = byte / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function linearToSrgb(v: number): number {
  const c = v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
  return Math.round(Math.min(1, Math.max(0, c)) * 255);
}

/** 位置参数 t（0–1，已 clamp）。五种几何的定义都在这一个函数里。 */
export function gradientT(spec: GradientSpec, x: number, y: number): number {
  const dx = spec.to.x - spec.from.x;
  const dy = spec.to.y - spec.from.y;
  const lengthSq = dx * dx + dy * dy;
  const px = x - spec.from.x;
  const py = y - spec.from.y;

  let t: number;
  if (lengthSq === 0) {
    t = 0;
  } else {
    switch (spec.kind) {
      case 'linear':
        t = (px * dx + py * dy) / lengthSq;
        break;
      case 'radial':
        t = Math.hypot(px, py) / Math.sqrt(lengthSq);
        break;
      case 'angle': {
        // 以 from→to 方向为 0°，顺时针一圈映射 0–1
        const angle = Math.atan2(py, px) - Math.atan2(dy, dx);
        t = ((angle / (2 * Math.PI)) % 1 + 1) % 1;
        break;
      }
      case 'reflected':
        // 以 from 为镜面：两侧对称地走完同一条带
        t = Math.abs(px * dx + py * dy) / lengthSq;
        break;
      case 'diamond': {
        // 旋转到渐变轴的坐标系里取 L1 距离（PS 的菱形语义）
        const length = Math.sqrt(lengthSq);
        const ux = dx / length;
        const uy = dy / length;
        const along = Math.abs(px * ux + py * uy);
        const across = Math.abs(-px * uy + py * ux);
        t = (along + across) / length;
        break;
      }
    }
  }
  t = Math.min(1, Math.max(0, t));
  return spec.reverse === true ? 1 - t : t;
}

/** 在色标间取色。返回 sRGB 字节 [r,g,b,a]。 */
export function gradientColorAt(spec: GradientSpec, t: number): [number, number, number, number] {
  const stops = spec.stops;
  if (stops.length === 0) return [0, 0, 0, 0];
  if (stops.length === 1 || t <= stops[0]!.offset) {
    return [...stops[0]!.color] as [number, number, number, number];
  }
  const last = stops[stops.length - 1]!;
  if (t >= last.offset) return [...last.color] as [number, number, number, number];

  let hi = 1;
  while (stops[hi]!.offset < t) hi++;
  const a = stops[hi - 1]!;
  const b = stops[hi]!;
  const span = b.offset - a.offset;
  const local = span === 0 ? 0 : (t - a.offset) / span;

  const out: [number, number, number, number] = [0, 0, 0, 0];
  for (let c = 0; c < 3; c++) {
    if (spec.space === 'linear') {
      const va = srgbToLinear(a.color[c]!);
      const vb = srgbToLinear(b.color[c]!);
      out[c] = linearToSrgb(va + (vb - va) * local);
    } else {
      out[c] = Math.round(a.color[c]! + (b.color[c]! - a.color[c]!) * local);
    }
  }
  // alpha 永远线性插值：它不是光量
  out[3] = Math.round(a.color[3]! + (b.color[3]! - a.color[3]!) * local);
  return out;
}

/** 逐像素采样入口：给 apply/着色器对拍共用。 */
export function sampleGradient(
  spec: GradientSpec,
  x: number,
  y: number,
): [number, number, number, number] {
  // 像素中心采样，与 GPU 片元坐标一致
  const color = gradientColorAt(spec, gradientT(spec, x + 0.5, y + 0.5));
  if (spec.dither !== true) return color;
  const offset = ditherOffset(x, y);
  return [
    Math.min(255, Math.max(0, Math.round(color[0] + offset))),
    Math.min(255, Math.max(0, Math.round(color[1] + offset))),
    Math.min(255, Math.max(0, Math.round(color[2] + offset))),
    color[3],
  ];
}
